import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Archive, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { API_URL, errMsg, fmtDateTime, fmtPLN } from './shared';

const download = async () => {
  const res = await axios.get(`${API_URL}/godziny/legacy/download`, { responseType: 'blob' });
  const url = URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = url; a.download = 'archiwum_kadry.xlsx'; a.click();
  URL.revokeObjectURL(url);
};

const Preview = ({ d }) => (
  <div className="text-sm space-y-2 text-[var(--crm-text-secondary)]" data-testid="legacy-preview">
    <p>Do usunięcia: <strong data-testid="legacy-workers-count">{d.workers_count}</strong> pracowników i <strong data-testid="legacy-logs-count">{d.logs_count}</strong> wpisów godzin
      {d.date_from && <> z okresu {d.date_from} – {d.date_to}</>}, łączny koszt robocizny <strong>{fmtPLN(d.total_cost)}</strong>.</p>
    {d.creators.length > 0 && <p>Wpisali: {d.creators.map((c) => `${c.initials} (${c.count})`).join(', ')}</p>}
    {d.projects.length > 0 && (
      <table className="w-full text-xs"><thead><tr className="text-left text-[var(--crm-text-muted)]">
        <th className="p-1">Zlecenie</th><th className="p-1 text-right">Robocizna</th><th className="p-1 text-right">Marża netto przed</th><th className="p-1 text-right">po usunięciu</th></tr></thead>
        <tbody>{d.projects.map((p) => (
          <tr key={p.id}><td className="p-1">{p.nazwa} – {p.klient} <span className="text-[var(--crm-text-muted)]">({p.created_at}, #{p.id.slice(0, 6)})</span></td><td className="p-1 text-right">{fmtPLN(p.legacy_labor)}</td>
            <td className="p-1 text-right">{fmtPLN(p.net_margin_before)}</td><td className="p-1 text-right text-emerald-500">{fmtPLN(p.net_margin_after)}</td></tr>))}
        </tbody></table>
    )}
  </div>
);

export const LegacyArchiveCard = () => {
  const [d, setD] = useState(null);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => axios.get(`${API_URL}/godziny/legacy/preview`).then((r) => setD(r.data)).catch(() => setD(null)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  if (d.done) {
    if (!d.file_available) return null;
    return (
      <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4 flex flex-wrap items-center gap-3" data-testid="legacy-done-card">
        <span className="text-sm text-[var(--crm-text-secondary)]">Archiwum starych Kadr ({fmtDateTime(d.done_at)}, {d.done_by}) – dostępne do {fmtDateTime(d.expires_at)}.</span>
        <Button size="sm" variant="outline" onClick={download} data-testid="legacy-download-btn"><Download className="w-4 h-4 mr-1" />Pobierz Excel</Button>
      </Card>
    );
  }
  if (!d.workers_count && !d.logs_count) return null;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/godziny/legacy/archive`, { confirm });
      toast.success(`Zarchiwizowano i usunięto: ${data.deleted_workers} pracowników, ${data.deleted_logs} wpisów`);
      load();
    } catch (e) { toast.error(errMsg(e, 'Błąd archiwizacji')); } finally { setBusy(false); }
  };
  return (
    <Card className="bg-amber-500/10 border-amber-500/40 p-4 space-y-3" data-testid="legacy-archive-card">
      <button type="button" onClick={() => setOpen(!open)} className="w-full text-left font-semibold text-[var(--crm-text-primary)] flex items-center gap-2"
        data-testid="legacy-archive-toggle">
        <Archive className="w-4 h-4" />Zarchiwizuj i wyczyść stare Kadry
        <span className="text-xs font-normal text-[var(--crm-text-secondary)]">({d.workers_count} pracowników, {d.logs_count} wpisów) – {open ? 'zwiń' : 'rozwiń'}</span>
      </button>
      {open && <>
      <Preview d={d} />
      <p className="text-xs text-amber-600">Najpierw powstanie plik Excel (do pobrania przez 30 dni), dopiero potem dane zostaną usunięte. Marże netto zleceń wzrosną o usuniętą robociznę.</p>
      <div className="flex flex-wrap gap-2 items-center">
        <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Wpisz USUN" className="w-40" data-testid="legacy-confirm-input" />
        <Button onClick={run} disabled={busy || confirm.trim().toUpperCase() !== 'USUN'} className="bg-red-600 hover:bg-red-700 text-white" data-testid="legacy-archive-btn">
          {busy ? 'Archiwizowanie...' : 'Zarchiwizuj i wyczyść'}
        </Button>
      </div>
      </>}
    </Card>
  );
};
